import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FamilyBar } from '@beyond-js/ui/react';
import wordmark from '@brand/beyond-logo-wordmark.svg';
import { useDocs } from '@cdn/docs';
import { useFamily, useFamilyLabels, useOrganization } from '@cdn/family';
import { useI18n } from '@cdn/i18n';
import { useSession } from '@cdn/identity';
import { Paths } from '@cdn/navigation';
import { NotificationBell } from '@cdn/notifications';
import { useTheme } from './theme-context';

/**
 * The family bar of every signed-in view (decisions D01 and D04): the same bar in every Beyond product, drawn
 * by `@beyond-js/ui`. It renders the descriptor Beyond Projects answers for the place in view, which the
 * service relays; while it loads, or when there is none (this installation runs without Projects, or Projects
 * did not answer), it shows what this application knows itself.
 *
 * The organization menu is how a person changes organization in CDN (D03): every row leads to CDN's own
 * arrival for it, from the descriptor or, without one, from the person's memberships in CDN's session. The
 * project menu is CDN's project chooser (Q22 a); "All projects" leads to CDN's projects page, and when Projects
 * did not answer the menu says so once, with a way to ask again. CDN annotates no project state of its own:
 * every member reads CDN and its references are the mappings Projects counts (`here.mapped`).
 *
 * The profile menu's product group starts with "Language and appearance" (decision D54), the family's one
 * dialog over CDN's `Preferences`: both come from the person's account on arrival (decision D07), a change made
 * there applies on this device only, and its "Change for all of Beyond" leads to the Accounts page where the
 * person changes them for every product. The menu ends with "Sign out of Beyond" (Q09): the bar's
 * `{ end, after }` form, which ends CDN's session and then leaves for Accounts' `/leave`. The bar never offers a
 * "Sign in" of its own: a session that ends while the person works is the page's `Session` dialog, which cannot be
 * dismissed (D63).
 */
export function Bar() {
	const { t, locale } = useI18n();
	const session = useSession();
	const family = useFamily();
	const labels = useFamilyLabels();
	const theme = useTheme();
	const docs = useDocs();
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const organization = session.organization;
	const origin = window.location.origin;

	// The home path declares its own place (`Home`): an arrival names the project or organization it continues in,
	// so the bar's first read is that place, never the person's default organization drawn and then replaced
	useOrganization(pathname === '/' ? null : organization?.organization);

	const brand = useMemo(() => ({ src: wordmark, href: docs.home ?? '/' }), [docs]);
	const user = session.user;
	// What the bar shows without a descriptor: the names and addresses this application knows itself. A
	// descriptor's own addresses win when it arrives.
	const fallback = {
		person: user ? { name: user.name ?? user.email ?? user.id, email: user.email ?? undefined } : null,
		organization: organization?.name ?? null,
		organizations: session.organizations.map(membership => ({
			id: membership.organization,
			name: membership.name,
			role: membership.role,
			url: `${origin}${new Paths(membership.organization).home}`,
			current: membership.organization === organization?.organization
		})),
		links: {
			home: docs.home,
			account: docs.account,
			docs: docs.page('cdn', locale.code),
			projects: organization && !family.standalone ? `${origin}${new Paths(organization.organization).projects}` : undefined,
			manage: docs.manage(organization?.organization),
			leave: docs.departure
		}
	};
	const notice = family.unanswered ? { text: t('family.notice.unanswered'), action: { label: t('family.notice.retry'), onSelect: () => family.retry() } } : null;
	const everywhere = docs.everywhere(`${origin}${pathname}${search}`) ?? null;
	const preferences = useMemo(() => ({ preferences: theme.preferences, everywhere }), [theme.preferences, everywhere]);
	// Sign out of Beyond (Q09): the bar ends CDN's session (`end`, bounded), then goes to Accounts' `/leave` —
	// the descriptor's `links.leave`, or this deployment's Accounts — with `product=cdn` and the way back. Without
	// either it signs out of CDN only (`after`). Unsent forms are drafts on this device: nothing to ask `before`.
	const signout = useMemo(() => ({ end: () => session.end(), after: () => session.close() }), [session]);

	return (
		<FamilyBar
			product="cdn"
			brand={brand}
			descriptor={family.descriptor}
			fallback={fallback}
			notice={notice}
			labels={labels}
			notifications={<NotificationBell />}
			account={{ signout, preferences }}
			onNavigate={item => {
				const url = new URL(item.url);
				navigate(`${url.pathname}${url.search}${url.hash}`);
			}}
		/>
	);
}
